/**
 * Renderer dei documenti legali. Switch puro su `BloccoTesto.tipo`.
 *
 * Nessun parser Markdown, nessun `dangerouslySetInnerHTML`, nessuna
 * dipendenza extra. I blocchi arrivano gia' tipizzati dal registro
 * (portale via callable, gestionale via la stessa forma).
 *
 * Importabile da entrambe le entry: questo file non dipende da Firebase,
 * dal router, ne' da nulla di specifico di `src/app` o `src/paziente`.
 */

import type { BloccoTesto } from './documento-legale.types';

export interface DocumentoLegaleViewProps {
  blocchi: readonly BloccoTesto[];
  /** Prefisso id per ancorare i heading (accessibilita', stampa). */
  idPrefix?: string;
}

function HeadingBlock({
  livello,
  testo,
  id,
}: {
  livello: 1 | 2 | 3;
  testo: string;
  id?: string;
}) {
  const className =
    livello === 1
      ? 'font-semibold text-text-main text-lg mt-4 mb-2 first:mt-0'
      : livello === 2
        ? 'font-semibold text-text-main text-base mt-3 mb-1.5'
        : 'font-medium text-text-main text-sm mt-2 mb-1';

  if (livello === 1) {
    return (
      <h2 id={id} className={className}>
        {testo}
      </h2>
    );
  }
  if (livello === 2) {
    return (
      <h3 id={id} className={className}>
        {testo}
      </h3>
    );
  }
  return (
    <h4 id={id} className={className}>
      {testo}
    </h4>
  );
}

function BloccoView({
  blocco,
  index,
  idPrefix,
}: {
  blocco: BloccoTesto;
  index: number;
  idPrefix: string;
}) {
  switch (blocco.tipo) {
    case 'heading':
      return (
        <HeadingBlock
          livello={blocco.livello}
          testo={blocco.testo}
          id={`${idPrefix}-h-${index}`}
        />
      );
    case 'paragraph':
      return (
        <p className="text-sm text-text-main leading-relaxed whitespace-pre-line mb-2">
          {blocco.testo}
        </p>
      );
    case 'list':
      return (
        <ul className="list-disc pl-5 mb-2 space-y-1">
          {blocco.voci.map((voce, i) => (
            <li
              key={`${idPrefix}-li-${index}-${i}`}
              className="text-sm text-text-main leading-relaxed whitespace-pre-line"
            >
              {voce}
            </li>
          ))}
        </ul>
      );
    case 'checkbox-list':
      return (
        <ul className="list-none pl-0 mb-2 space-y-1.5">
          {blocco.voci.map((voce, i) => (
            <li
              key={`${idPrefix}-cb-${index}-${i}`}
              className="flex gap-2 items-start text-sm text-text-main leading-relaxed"
            >
              <span
                aria-hidden="true"
                className="mt-0.5 inline-block h-4 w-4 shrink-0 rounded border border-outline bg-surface-container-lowest"
              />
              <span className="whitespace-pre-line">{voce}</span>
            </li>
          ))}
        </ul>
      );
    default: {
      const _esaustivo: never = blocco;
      return _esaustivo;
    }
  }
}

export function DocumentoLegaleView({
  blocchi,
  idPrefix = 'doc-legale',
}: DocumentoLegaleViewProps) {
  return (
    <div className="text-text-main">
      {blocchi.map((blocco, index) => (
        <BloccoView
          key={`${idPrefix}-${index}-${blocco.tipo}`}
          blocco={blocco}
          index={index}
          idPrefix={idPrefix}
        />
      ))}
    </div>
  );
}
